import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Typography,
} from "@mui/material";
import type { PaymentAccount } from "../../api/payments";

interface StripeAccountCardProps {
  account: PaymentAccount | null;
  isLoading: boolean;
  isRedirecting: boolean;
  onConnectStripe: () => void;
  onOpenDashboard: () => void;
}

export const StripeAccountCard: React.FC<StripeAccountCardProps> = ({
  account,
  isLoading,
  isRedirecting,
  onConnectStripe,
  onOpenDashboard,
}) => {
  if (isLoading) {
    return (
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Skeleton variant="text" width="40%" height={28} />
          <Skeleton variant="text" width="70%" sx={{ mt: 1 }} />
          <Skeleton variant="rectangular" width={140} height={36} sx={{ mt: 2 }} />
        </CardContent>
      </Card>
    );
  }

  if (account?.payments_admin_disabled) {
    return (
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="h6">Stripe Account</Typography>
            <Chip label="Disabled by admin" color="default" size="small" />
          </Box>
          <Typography variant="body2" color="text.secondary">
            Payments have been disabled by your account administrator. Contact
            support to re-enable.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (account === null) {
    return (
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="h6">Stripe Account</Typography>
            <Chip label="Not connected" color="default" size="small" />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Accept online payments by connecting your Stripe account.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            disabled={isRedirecting}
            onClick={onConnectStripe}
            aria-label="Connect Stripe"
          >
            {isRedirecting ? "Redirecting…" : "Connect Stripe"}
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (
    account.account_status === "onboarding_incomplete" ||
    account.account_status === "pending_verification" ||
    account.account_status === "restricted"
  ) {
    return (
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="h6">Stripe Account</Typography>
            <Chip label="Action required" color="warning" size="small" />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Additional information is required to activate payouts. Complete
            setup in Stripe.
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              disabled={isRedirecting}
              onClick={onConnectStripe}
              aria-label="Continue setup"
            >
              {isRedirecting ? "Redirecting…" : "Continue setup"}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={onOpenDashboard}
              aria-label="Open Stripe Dashboard"
            >
              Open Stripe Dashboard
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="h6">Stripe Account</Typography>
          <Chip label="Payments enabled" color="success" size="small" />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Charges and payouts are active.
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          onClick={onOpenDashboard}
          aria-label="Open Stripe Dashboard"
        >
          Open Stripe Dashboard
        </Button>
      </CardContent>
    </Card>
  );
};
